import { isNull } from "lodash/fp";

export type AttendanceType =
  | "onTime"
  | "late"
  | "conferenceRoom"
  | "missed"
  | "declined";

export interface Meeting {
  lateBy?: number | null;
  isMissed?: boolean;
  hasConferenceRoom: boolean;
  instances?: Meeting[];
}

export const getAttendanceType = <T extends Meeting>(
  meeting: T
): AttendanceType | null => {
  if (!isNull(meeting.lateBy) && meeting.lateBy! < 60) return "onTime";
  if (!isNull(meeting.lateBy) && meeting.lateBy! >= 60) return "late";
  if (meeting.isMissed) {
    // TODO (ENG-4442) - detect whether a meeting was "declined"
    return meeting.hasConferenceRoom ? "conferenceRoom" : "missed";
  }
  return null;
};

const getLateBy = <T extends Meeting>(meeting: T) => {
  if (meeting.isMissed) return Infinity;
  return meeting.lateBy ?? 0;
};

// the order in which we show/sort the attendance types
export const ATTENDANCE_TYPES_IN_ORDER: AttendanceType[] = [
  "onTime",
  "conferenceRoom",
  "late",
  "missed",
  "declined",
];

/**
 * Comparator that will handle sorting attendance based on the attendance
 * type's relative value, where:
 * On Time > Conference Room > Late (internally sorted in asc order
 * by amount late) > Missed > Declined
 * */
export const attendanceCompareBy = <T extends Meeting>(
  meetingA: T,
  meetingB: T
) => {
  const attendanceTypeA = getAttendanceType(meetingA);
  const attendanceTypeB = getAttendanceType(meetingB);
  if (attendanceTypeA === attendanceTypeB) {
    if (attendanceTypeA === "late") {
      return getLateBy(meetingB) - getLateBy(meetingA);
    }
    return 0;
  }

  const priorityA = ATTENDANCE_TYPES_IN_ORDER.findIndex(
    (type) => type === attendanceTypeA
  );
  const priorityB = ATTENDANCE_TYPES_IN_ORDER.findIndex(
    (type) => type === attendanceTypeB
  );

  const meetingAValue = ATTENDANCE_TYPES_IN_ORDER.length - priorityA;
  const meetingBValue = ATTENDANCE_TYPES_IN_ORDER.length - priorityB;
  return meetingAValue - meetingBValue;
};
